import { backendApiService } from "@/lib/services/backendApiService";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { useCallback, useMemo, useState } from "react";

type Props = {
  treeId: string;
  buttonLabel?: string;
};

export default function AiSummaryButton({
  treeId,
  buttonLabel = "AI Summary",
}: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const defaultGetAiSummary = async (id: string) => {
    // Replace with your real endpoint
    const res = await backendApiService.generateAISummary({id: id});
    if (!res.ok) {
      throw new Error(`Request failed`);
    }
    // expecting { summary: string } — adjust to your API shape
    return res.data.summary ?? "";
  };

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSummary(null);
    try {
      const fn = defaultGetAiSummary;
      const result = await fn(treeId);
      setSummary(result);
    } catch (err: any) {
      setError(err?.message ?? "Failed to get summary");
    } finally {
      setLoading(false);
    }
  }, [treeId]);

  const sanitizedHtml = useMemo(() => {
    const parsed = marked.parse(summary ?? "");
    if (typeof parsed === 'string') {
      return DOMPurify.sanitize(parsed);
    }
    // if marked.parse returned a Promise for some reason, return empty string
    return '';
  }, [summary]);

  const handleOpen = async () => {
    setOpen(true);
    await fetchSummary();
  };

  const handleClose = () => {
    setOpen(false);
    setSummary(null);
    setError(null);
    setLoading(false);
  };

  return (
    <>
      <Button variant="outlined" color="primary" onClick={handleOpen}>
        {buttonLabel}
      </Button>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle>
          AI Summary
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading && (
            <div style={{ display: "flex", justifyContent: "center", padding: 16 }}>
              <CircularProgress />
            </div>
          )}

          {!loading && error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && summary !== null && (
            <div>
              <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
            </div>
          )}

          {!loading && !error && summary === null && (
            <Typography variant="body2" color="text.secondary">
              No summary available.
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={fetchSummary} disabled={loading}>
            Retry
          </Button>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}